<template>
  <div class="page-content">
    <modal-hotel-suppliers />
    <modal-hotel-mappings />
    <modal-abs v-if="isABS" />

    <div
      v-if="!isLoading"
      class="page-content__container"
    >
      <content-page-search :title="hotel.displayName" />
      <section class="page-content__hotel">
        <header class="hotel__header">
          <div class="hotel__header-row">
            <h1 class="hotel__title">
              {{ hotel.displayName }}
            </h1>
            <div class="hotel__rating">
              <stars
                :stars="hotel.starRating"
                class="stars"
              />
            </div>
            <modal-toggler
              class="modal-toggler"
              @click="$modal.show('modal-hotel-mappings', modalHotelMappingsData)"
            />
          </div>
          <div class="hotel__header-row">
            <div class="hotel__location">
              <img
                :src="require('@/assets/icons/icon-location.svg')"
                alt=""
                width="10"
                height="15"
              >
              <span>{{ getFullHotelLocation(hotel) }}</span>
            </div>
          </div>
        </header>
        <div class="hotel__container">
          <div class="hotel__gallery">
            <gallery
              :images="images"
              url-field="imageUrl"
              text-field="description"
              @toggle-modal="$modal.show('modal-hotel-suppliers', { ...modalHotelSuppliersData, activeTab: 'photos' })"
            />
          </div>
          <div class="hotel__text">
            <section
              v-if="descriptions"
              class="hotel__information"
            >
              <h2 class="subtitle">
                <span>Information</span>
                <modal-toggler
                  class="modal-toggler"
                  @click="$modal.show('modal-hotel-suppliers', { ...modalHotelSuppliersData, activeTab: 'descriptions' })"
                />
              </h2>
              <read-more-text :text="descriptions.en" />
            </section>
            <section class="hotel__facilities">
              <h2 class="subtitle">
                <span>Facilities</span>
                <modal-toggler
                  class="modal-toggler"
                  @click="$modal.show('modal-hotel-suppliers', { ...modalHotelSuppliersData, activeTab: 'facilities' })"
                />
              </h2>
              <ul class="list-facilities">
                <li
                  v-for="facility in hotel.topFacilities"
                  :key="facility.id"
                  class="list-facilities__item"
                >
                  <div class="facility">
                    <span
                      class="facility__icon"
                      v-html="require(`!svg-inline-loader?removeTags=true!@/assets/icons/facilities/hotel/${facility.id}.svg`)"
                    />
                    <span>{{ facility.label }}</span>
                  </div>
                </li>
              </ul>
            </section>
          </div>
        </div>
      </section>
      <section
        v-if="!isABS"
        class="page-content__rooms rooms"
      >
        <header class="rooms__header">
          <h2 class="rooms__title">
            From ${{ hotel.minPrice }}
          </h2>
          <div class="rooms__switchers">
            <form-switcher v-model="switcher.isBestAvailable">
              Best Available
            </form-switcher>
            <form-switcher v-model="switcher.isDynamicMarkup">
              Dynamic Markup
            </form-switcher>
            <form-switcher v-model="switcher.isRoomMapping">
              Room Mapping
            </form-switcher>
          </div>
        </header>
        <div class="rooms__content">
          <table
            class="table-rooms"
            :class="{
              'view-no-room': isNoRoom,
              'view-room-mapping': switcher.isRoomMapping,
              'view-best-available': switcher.isBestAvailable,
              'view-dynamic-markup': switcher.isDynamicMarkup,
            }"
          >
            <colgroup v-if="isNoRoom">
              <col width="1%">
              <col>
              <col width="1%">
            </colgroup>
            <colgroup v-if="switcher.isRoomMapping">
              <col width="314">
              <col width="220">
              <col width="1%">
              <col width="20%">
              <col>
            </colgroup>
            <thead>
              <tr v-if="isNoRoom">
                <th class="ta-center">
                  Supplier
                </th>
                <th>Room</th>
                <th>Price per night</th>
              </tr>
              <tr v-if="switcher.isRoomMapping">
                <th>Room type</th>
                <th>Supplier</th>
                <th>Price per night</th>
                <th>Options</th>
                <th>Original Name</th>
              </tr>
            </thead>
            <tbody v-if="isNoRoom">
              <tr
                v-for="room in roomsFlat.sort((room1, room2) => room1.price - room2.price)"
                :key="getRoomUniqueKey(room)"
              >
                <td class="ta-center">
                  {{ room.supplier }}
                </td>
                <td>
                  {{ room.supplierRoomName }}
                </td>
                <td><span class="price">${{ room.price }}</span></td>
              </tr>
            </tbody>

            <template v-if="switcher.isRoomMapping">
              <tbody
                v-for="(roomType, roomKey, $index) in rooms"
                :key="roomKey"
                :class="{
                  'has-no-dynamic-markup': switcher.isDynamicMarkup && getFilteredRooms(roomType.rooms).every(room => !room.dynamicMarkup),
                }"
              >
                <tr
                  v-for="(room, index) in getFilteredRooms(roomType.rooms)"
                  v-if="switcher.isDynamicMarkup ? !room.dynamicMarkup.isHidden : true"
                  :key="getRoomUniqueKey(room)"
                  :class="{
                    [`has-${room.dynamicMarkup.price ? '' : 'no-'}dynamic-markup${room.dynamicMarkup ? '-price' : ''}`]: switcher.isDynamicMarkup,
                  }"
                >
                  <td
                    v-if="switcher.isDynamicMarkup ? (getFilteredRooms(roomType.rooms).filter(room => !room.dynamicMarkup.isHidden && room.dynamicMarkup.price).indexOf(room) === 0) : index === 0"
                    :rowspan="switcher.isDynamicMarkup ? (getFilteredRooms(roomType.rooms).filter(room => !room.dynamicMarkup.isHidden)).length + 1 : getFilteredRooms(roomType.rooms).length + 1"
                  >
                    <h2>{{ roomType.rooms[0].roomName }}</h2>
                    <div class="room-type-visual">
                      <img
                        :src="getRoomTypePhoto(roomKey, $index)"
                        alt="Room Photo"
                        @click="$modal.show('modal-hotel-mappings', {
                          ...modalHotelMappingsData,
                          activeTab: 'all-rooms',
                          roomKey,
                        })"
                      >
                    </div>
                    <ul class="list-facilities">
                      <li
                        v-for="amenity in roomType.topAmenities"
                        :key="amenity.id"
                        class="list-facilities__item"
                      >
                        <div class="facility">
                          <span
                            class="facility__icon"
                            v-html="require(`!svg-inline-loader?removeTags=true!@/assets/icons/facilities/room/${amenity.id}.svg`)"
                          />
                          <span>{{ amenity.label }}</span>
                        </div>
                      </li>
                    </ul>
                  </td>
                  <td class="ta-center cell-logo-supplier">
                    <img
                      v-if="room.supplier"
                      :src="`/images/suppliers/logos/${suppliersListByName[room.supplier]}.png` || null"
                      :alt="room.supplier"
                      height="40"
                      class="logo-supplier"
                    >
                    <span v-else>N/A</span>
                    <template v-if="switcher.isDynamicMarkup && room.dynamicMarkup">
                      <span
                        class="dynamic-ribbon"
                        :class="{
                          [`is-${room.dynamicMarkup.price ? 'net' : 'gross'}`]: true,
                        }"
                      >
                        <template v-if="room.dynamicMarkup.price">N</template>
                        <template v-else>G</template>
                      </span>
                    </template>
                  </td>
                  <td class="cell-price ta-center">
                    <span
                      class="price"
                      :class="{
                        'is-crossed-out': switcher.isDynamicMarkup && room.dynamicMarkup && room.dynamicMarkup.price,
                      }"
                    >${{ room.price }}</span>
                    <br>
                    <span
                      v-if="switcher.isDynamicMarkup && room.dynamicMarkup && room.dynamicMarkup.price"
                      class="price"
                      :class="{
                        'is-dynamic-markup-price': room.dynamicMarkup.price,
                      }"
                      @click="toggleOtherPrices(room, getFilteredRooms(roomType.rooms))"
                    >${{ room.dynamicMarkup.price }}</span>
                  </td>
                  <td>
                    <ul>
                      <li>
                        {{ room.roomBasis }}
                      </li>
                      <li>
                        <template v-if="room.refundability === 'NRF'">
                          Non
                        </template>
                        Refundable
                      </li>
                      <li v-if="room.payment && room.payment.length > 0">
                        Payment: {{ room.payment }}
                      </li>
                    </ul>
                  </td>
                  <td>
                    <div style="position: relative; padding-right: 30px;">
                      {{ room.supplierRoomName }}
                      <a
                        v-if="
                          switcher.isDynamicMarkup &&
                            !room.dynamicMarkup.price &&
                            getFilteredRooms(roomType.rooms)[index - 1] &&
                            getFilteredRooms(roomType.rooms)[index - 1].dynamicMarkup.price
                        "
                        href="#"
                        class="link-close"
                        @click.prevent="toggleOtherPrices(getFilteredRooms(roomType.rooms)[index - 1], getFilteredRooms(roomType.rooms))"
                      >
                        X
                      </a>
                    </div>
                  </td>
                </tr>
                <tr
                  :key="roomKey"
                  class="empty-row"
                >
                  <td />
                  <td />
                  <td />
                  <td />
                </tr>
              </tbody>
              <tbody>
                <tr class="empty-row">
                  <td />
                  <td />
                  <td />
                  <td />
                  <td />
                </tr>
              </tbody>
            </template>
          </table>
        </div>
      </section>
      <section
        v-if="isABS"
        class="page-content__abs"
      >
        <h2>Rooms</h2>
        <abs-room-card
          :id="absRoomData.id"
          :title="absRoomData.title"
          :price="absRoomData.price"
          :currency="absRoomData.currency"
          :main-img="absRoomData.mainImg"
          :small-img-gallery="absRoomData.images"
          :facilities-list="absRoomData.facilitiesList"
          :amenities-list="absRoomData.amenitiesList"
          :refundability="absRoomData.refundability"

          @trigger-modal="$modal.show('modal-hotel-abs')"
        />
      </section>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { pick, flatten, sortBy } from 'lodash';
import { getFullHotelLocation } from '@/helpers';
import AbsRoomCard from '@/components/AbsRoomCard';
import ContentPageSearch from '@/components/ContentPageSearch';
import Gallery from '@/components/Gallery';
import ReadMoreText from '@/components/ReadMoreText';
import ModalHotelSuppliers from '@/components/modals/ModalHotelSuppliers';
import ModalHotelMappings from '@/components/modals/ModalHotelMappings';
import ModalAbs from '@/components/modals/ModalAbs';

export default {
  name: 'page-content',
  components: {
    AbsRoomCard,
    ContentPageSearch,
    Gallery,
    ReadMoreText,
    ModalHotelSuppliers,
    ModalHotelMappings,
    ModalAbs,
  },
  data() {
    return {
      suppliersList: null,
      hotel: null,
      images: null,
      facilities: null,
      descriptions: null,
      rooms: null,
      isLoading: false,
      switcher: {
        isBestAvailable: false,
        isDynamicMarkup: false,
        isRoomMapping: false,
      },
      grossSuppliersList: [
        'Agoda',
        'Amadeus',
        'Booking',
        'Expedia',
        'ExpediaSite',
        'HRS',
        'Priceline',
        'GetARoom',
      ],
      absRoomData: {
        id: 32756238756873,
        title: 'Standard Room',
        price: 249,
        currency: '$',
        mainImg: require('../assets/content/hotel-600.jpg'),
        smallImgGallery: [
          {
            imageUrl: require('../assets/content/1.jpg'),
            description: 'photo 1',
          },
          {
            imageUrl: require('../assets/content/2.jpg'),
            description: 'photo 2',
          },
          {
            imageUrl: require('../assets/content/3.jpg'),
            description: 'photo 3',
          },
        ],
        refundability: 'RF',
        facilitiesList: [
          {
            id: 'sqm',
            label: '700 sqm',
          },
          {
            id: 'bedding',
            label: '2 Double',
          },
          {
            id: 'air-conditioning',
            label: 'Air Conditioning',
          },
          {
            id: 'hair-dryer',
            label: 'Hair dryer',
          },
          {
            id: 'iron-ironing-board',
            label: 'Iron/ironing board',
          },
        ],
        amenitiesList: [
          {
            id: 1,
            title: 'View',
            imgUrl: require('../assets/content/ocean-256.jpg'),
            description: 'Pick the view of your preference: want to look at the hustle and bustle of the city life or prefer the calm and serenity of the ocean.',
            options: [
              {
                name: 'City',
                price: 160,
              },
              {
                name: 'Ocean',
                price: 175,
              },
            ],
            selectedPrice: null,
          },
          {
            id: 2,
            title: 'Room Class',
            imgUrl: require('../assets/content/bed-256.jpg'),
            description: 'Choose the room most suitable to your needs. Feel comfortable in our Premier rooms or get luxurious in our wonderful suites.',
            options: [
              {
                name: 'Premier',
                price: 35,
              },
              {
                name: 'Club',
                price: 200,
              },
              {
                name: 'Suite',
                price: 280,
              },
              {
                name: 'Apartment',
                price: 1800,
              },
            ],
            selectedPrice: null,
          },
          {
            id: 3,
            title: 'Meal Plan',
            imgUrl: require('../assets/content/meal-256.jpg'),
            description: 'Pick the right meal for you. We have a choice for even the most discerning foodie.',
            options: [
              {
                name: 'Bed and Breakfast',
                price: 40,
              },
              {
                name: 'Continental Breakfast',
                price: 50,
              },
              {
                name: 'Full Board',
                price: 60,
              },
            ],
            selectedPrice: null,
          },
          {
            id: 4,
            title: 'Balcony',
            imgUrl: require('../assets/content/balcony-256.jpg'),
            description: 'Choose a room with a balcony, if you want to get a feel of the city from the comfort of your room. Or if you\'re smoking, we don\'t judge.',
            price: 115,
          },
        ],
      },
    };
  },
  computed: {
    suppliersListByName() {
      const result = {};
      this.suppliersList.forEach(({ id, label }) => {
        result[label] = id;
      });
      return result;
    },
    roomsFlat() {
      return flatten(Object.keys(this.rooms).map(roomKey => this.rooms[roomKey].rooms));
    },
    isABS() {
      return this.$route.query && this.$route.query.abs;
    },
    isNoRoom() {
      return !this.switcher.isRoomMapping && !this.switcher.isBestAvailable && !this.switcher.isDynamicMarkup;
    },
    bestAvailableQty() {
      return Object.values(this.rooms).reduce((acc, group) => {
        return acc + this.getFilteredRooms(group.rooms).length;
      }, 0);
    },
    modalHotelSuppliersData() {
      return {
        hotel: this.hotel,
        images: this.images,
        facilities: this.facilities,
        descriptions: this.descriptions,
      };
    },
    modalHotelMappingsData() {
      return {
        hotel: this.hotel,
        rooms: this.rooms,
        roomImages: this.roomImages,
      };
    },
  },
  watch: {
    '$route.params.id': {
      async handler(val) {
        const loader = this.$loading.show();
        this.isLoading = true;

        await this.init(val);

        loader.hide();
        this.isLoading = false;
      },
    },
    'switcher.isBestAvailable': {
      handler(val) {
        if (val) {
          this.switcher.isDynamicMarkup = false;
          this.switcher.isRoomMapping = true;
          this.$notify({ clean: true });
          this.$notify({
            text: `Showing ${this.bestAvailableQty} rooms of ${this.roomsFlat.length}`,
          });
        } else {
          this.$notify({ clean: true });
          this.$notify({
            text: 'Showing all rooms',
          });
        }
        this.$ls.set('best-available', val);
      },
    },
    'switcher.isDynamicMarkup': {
      handler(val) {
        if (val) {
          this.switcher.isBestAvailable = false;
          this.switcher.isRoomMapping = true;
        }
        this.$ls.set('dynamic-markup', val);
      },
    },
    'switcher.isRoomMapping': {
      handler(val) {
        if (!val) {
          this.switcher.isBestAvailable = false;
          this.switcher.isDynamicMarkup = false;
        }
        this.$ls.set('room-mapping', val);
      },
    },
  },
  async created() {
    const loader = this.$loading.show();
    this.isLoading = true;

    const [suppliersList] = await Promise.all([
      this.getSuppliersList(),
      this.init(),
    ]);

    this.suppliersList = suppliersList;

    loader.hide();
    this.isLoading = false;
  },
  methods: {
    getFullHotelLocation,
    ...mapActions([
      'getHotel',
      'getHotelImages',
      'getHotelFacilities',
      'getHotelDescriptions',
      'getHotelRooms',
      'getRoomImages',
      'getSuppliersList',
    ]),
    async init(id = this.$route.params.id) {
      const [
        hotel,
        images,
        facilities,
        descriptions,
        rooms,
        roomImages,
      ] = await Promise.all([
        this.getHotel(id),
        this.getHotelImages(id),
        this.getHotelFacilities(id),
        this.getHotelDescriptions(id),
        this.getHotelRooms(id),
        this.getRoomImages(id),
      ]);

      this.hotel = hotel;
      this.images = images;
      this.facilities = facilities;
      this.descriptions = descriptions;
      this.rooms = rooms;
      this.roomImages = roomImages;

      const dynamicMarkupGroups = {};

      Object.keys(this.rooms).forEach(roomKey => {
        if (!dynamicMarkupGroups[roomKey]) {
          dynamicMarkupGroups[roomKey] = {};
        }

        this.rooms[roomKey].rooms = this.rooms[roomKey].rooms
          .filter((room, i, arr) => {
            return arr.indexOf(
              arr.find(t => this.getRoomUniqueKey(t) === this.getRoomUniqueKey(room))) === i;
          })
          .map(room => ({
            ...room,
            dynamicMarkup: {
              isHidden: false,
              price: null,
            },
          }))
          .sort((roomA, roomB) => {
            const keyA = [
              roomA.roomBasis,
              roomA.refundability,
            ].join('');
            const keyB = [
              roomB.roomBasis,
              roomB.refundability,
            ].join('');
            return keyA < keyB;
          });

        this.rooms[roomKey].rooms.forEach(room => {
          const key = [
            room.roomBasis,
            room.refundability,
          ].join('');

          if (!dynamicMarkupGroups[roomKey][key]) {
            dynamicMarkupGroups[roomKey][key] = [];
          }

          const isGross = this.grossSuppliersList.indexOf(room.supplier) > -1;

          // First row must be Net and if already one exists then Gross rows
          if ((dynamicMarkupGroups[roomKey][key].length === 0 && !isGross) ||
              (dynamicMarkupGroups[roomKey][key].length && isGross)) {
            dynamicMarkupGroups[roomKey][key].push(room);
          } else {
            room.dynamicMarkup = false;
          }
        });
      });
      // console.log(dynamicMarkupGroups);
      Object.keys(dynamicMarkupGroups).forEach(roomKey => {
        const roomTypeGroups = dynamicMarkupGroups[roomKey];

        Object.values(roomTypeGroups).forEach(roomTypeGroup => {
          if (roomTypeGroup.length === 1) {
            roomTypeGroup.forEach(room => {
              room.dynamicMarkup = false;
            });
          }
          if (roomTypeGroup.length > 1) {
            const roomsSorted = roomTypeGroup.slice().sort((a, b) => {
              if (a.price < b.price) return -1;
              if (a.price > b.price) return 1;
              return 0;
            });

            const pricesUniqueList = Array.from(new Set(roomsSorted.map(({ price }) => price)));

            if (pricesUniqueList.length < 2) {
              roomTypeGroup.forEach(room => {
                room.dynamicMarkup = false;
              });
            } else {
              if ((pricesUniqueList[1] * 97 / 100).toFixed(2) > pricesUniqueList[0]) {
                roomTypeGroup.forEach(room => {
                  room.dynamicMarkup.price = pricesUniqueList[0] === room.price ? (pricesUniqueList[1] * 97 / 100).toFixed(2) : null;
                  room.dynamicMarkup.isHidden = pricesUniqueList[0] !== room.price;
                });
              } else {
                roomTypeGroup.forEach(room => {
                  room.dynamicMarkup = false;
                });
              }
            }
          }
        });
      });
    },
    toggleOtherPrices(room, rooms) {
      const targetOptionsKey = [
        room.roomBasis,
        room.refundability,
      ].join('');

      rooms.forEach(room => {
        const optionsKey = [
          room.roomBasis,
          room.refundability,
        ].join('');
        if (optionsKey === targetOptionsKey && room.dynamicMarkup && room.dynamicMarkup.price === null) {
          room.dynamicMarkup.isHidden = !room.dynamicMarkup.isHidden;
        }
      });
    },
    getFilteredRooms(rooms) {
      if (this.switcher.isBestAvailable) {
        return sortBy(this.filterBestAvailable(rooms), ['targetRoomKey', 'price']);
      }
      if (this.switcher.isDynamicMarkup) {
        return rooms
          .filter(room => room.dynamicMarkup)
          .slice()
          .sort((roomA, roomB) => {
            const aKey = [
              roomA.roomBasis,
              roomA.refundability,
            ].join('');
            const bKey = [
              roomB.roomBasis,
              roomB.refundability,
            ].join('');

            if (aKey < bKey) return -1;
            if (aKey > bKey) return 1;
            return roomA.price < roomB.price ? -1 : 1;
          });
      }
      return sortBy(rooms, ['targetRoomKey', 'price']);
    },
    getSupplierName(room) {
      const na = 'n/a';
      if (!room.supplierName) return na;
      if (room.supplierName === '#Н/Д') return na;
      return room.supplierName;
    },
    getRoomPhoto({ targetRoomKey }) {
      const images = this.roomImages[targetRoomKey] || [];
      if (images.length === 0) {
        return '/images/content/img-no-room-placeholder.jpg';
      }
      return images[0];
    },
    getRoomTypePhoto(roomKey, index) {
      const images = this.roomImages[roomKey];
      if (!images || images.length === 0) {
        return '/images/content/img-no-room-placeholder.jpg';
      }
      return images[index] || images[images.length - 1];
    },
    filterDynamicMarkup(rooms) {
      const dynamicMarkup = {};
      rooms.forEach(room => {
        const optionsKey = [
          room.roomBasis,
          room.refundability,
        ].join('');

        if (!dynamicMarkup[optionsKey]) {
          dynamicMarkup[optionsKey] = [];
        }
        dynamicMarkup[optionsKey].push(room);
      });
      Object.keys(dynamicMarkup).forEach(key => {
        if (dynamicMarkup[key].length < 2) return;

        const roomsSorted = dynamicMarkup[key].slice().sort((a, b) => {
          if (a.price < b.price) return -1;
          if (a.price > b.price) return 1;
          return 0;
        });

        const pricesUniqueList = Array.from(new Set(roomsSorted.map(({ price }) => price)));

        if (pricesUniqueList.length < 2) return;

        if ((pricesUniqueList[1] * 97 / 100).toFixed(2) > pricesUniqueList[0]) {
          dynamicMarkup[key] = dynamicMarkup[key].map(room => ({
            ...room,
            dynamicMarkupPrice: pricesUniqueList[0] === room.price ? (pricesUniqueList[1] * 97 / 100).toFixed(2) : null,
            hasDynamicMarkupPrice: pricesUniqueList[0] === room.price,
          }));
        }
      });
      return flatten(Object.values(dynamicMarkup));
    },
    filterBestAvailable(rooms) {
      const bestAvailable = {};
      rooms.forEach(room => {
        const optionsKey = [
          room.roomBasis,
          room.refundability,
          room.payment,
        ].join('');

        if (!bestAvailable[optionsKey]) {
          bestAvailable[optionsKey] = room;
        }
        if (room.price < bestAvailable[optionsKey].price) {
          bestAvailable[optionsKey] = room;
        }
      });
      return Object.values(bestAvailable);
    },
    getRoomUniqueKey(room) {
      return Object.values(pick(room, [
        'targetRoomKey',
        'roomBasis',
        // 'roomName',
        'price',
        'refundability',
      ])).join('-');
    },
  },
};
</script>

<style lang="scss">
.facility {
  color: #6e7c91;
  font-weight: 500;
  font-size: 13px;
  line-height: 16px;
  display: flex;
  align-items: center;
  & > * {
    margin: 0 7px;
    &:first-child {margin-left: 0;}
    &:last-child {margin-right: 0;}
  }
  white-space: nowrap;
  &__icon {
    width: 17px;
    height: 17px;
    display: flex;
    align-items: center;
    flex: 0 0 17px;
    color: #32325a;
    svg {
      display: block;
      width: 100%;
      height: 100%;
    }
  }
}
</style>
<style lang="scss" scoped>
.page-content {
  &__hotel,
  &__rooms,
  &__abs {
    padding: 0 24px;

    @media (min-width: 1024px) {
      padding: 0 50px;
    }
  }
  &__abs {
    & > h2 {
      font-size: 15px;
      line-height: 20px;
      font-weight: bold;
      color: #6e7c91;
      & + * {margin-top: 20px;}
    }
  }
  &__container {
    background-color: #fff;
    padding: 0 0 50px;
  }
}
.hotel {
  &__location {
    font-size: 14px;
    line-height: 20px;
    color: #6e7c91;
    font-weight: 500;
    display: flex;
    & > * {
      margin: 0 6px;
      &:first-child {margin-left: 0;}
      &:last-child {margin-right: 0;}
    }
    align-items: center;
  }
  &__container {
    padding-bottom: 30px;
    display: flex;
    & > * {
      margin: 0 15px;
      @media (min-width: 992px) {
        margin: 0 30px;
      }
      &:first-child {margin-left: 0;}
      &:last-child {margin-right: 0;}
    }
    justify-content: space-between;
  }
  &__gallery {
    padding-bottom: 8px;
    flex: 1 0 100%;
    max-width: 320px;

    @media (min-width: 992px) {
      max-width: 500px;
    }
  }
  &__text {
    flex: 1 1 auto;
    & > section + section {
      margin-top: 50px;
    }
    .subtitle {
      font-size: 15px;
      line-height: 24px;
      font-weight: bold;
      color: #6e7c91;
      & > * {
        margin: 0 6px;
        &:first-child {margin-left: 0;}
        &:last-child {margin-right: 0;}
      }
      display: flex;
      align-items: flex-start;
      & + * {margin-top: 22px;}
    }
  }
  &__header {
    padding: 44px 0 32px;
    &-row {
      display: flex;
      align-items: baseline;
      & + * {margin-top: 8px;}
      & > * {
        margin: 0 7px;
        &:first-child {margin-left: 0;}
        &:last-child {margin-right: 0;}
      }
    }
    .modal-toggler {align-self: center;}
    .stars {
      margin: 0;
    }
  }
  &__title {
    font-size: 26px;
    line-height: 30px;
    font-weight: bold;
    color: #32325a;
  }
  &__facilities {
    .list-facilities {
      display: flex;
      flex-wrap: wrap;
      & > * {
        margin: 0 40px 20px 0;
      }
      max-width: 600px;
    }
  }
}
.rooms {
  font-size: 14px;
  line-height: 20px;
  font-weight: 500;
  color: #6e7c91;
  &__content {
    overflow: auto;
  }
  &__header {
    background-color: #32325a;
    color: #fff;
    border-radius: 3px 3px 0 0;
    padding: 15px 35px 15px 25px;
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  &__title {
    font-size: 26px;
    line-height: 30px;
    font-weight: bold;
  }
  &__switchers {
    display: flex;
    justify-content: space-between;
    & > * {
      margin: 0 10px;
      &:first-child {margin-left: 0;}
      &:last-child {margin-right: 0;}
    }
  }
}

.table-rooms {
  border-top: 2px solid #d8dae3;
  width: 100%;
  tbody {
    &.has-no-dynamic-markup {
      display: none;
      opacity: 0.4;
      cursor: default;
    }
  }
  th {
    background-color: #eef1f6;
    color: #6e7c91;
    font-size: 12px;
    line-height: 20px;
    padding: 11px 14px;
    font-weight: bold;
    text-align: left;
    white-space: nowrap;
    &:first-child ~ th {
      border-left: 1px solid #d5dde9;
    }
  }
  &.view-no-room th:first-child + th {border-left: 0;}
  td {
    padding: 11px 14px;
    border-bottom: 1px solid #d5dde9;
    border-style: solid;
    border-color: #d5dde9;
    border-width: 1px 0 0 1px;
    vertical-align: middle;
    background-color: #f7f9fc;
    height: 1%;
    &:first-child {
      border-left-width: 0;
    }
    &.cell-logo-supplier {
      font-size: 0;
      line-height: 0;
      position: relative;
      & > img,
      & > span:not([class]) {
        font-size: 14px;
        line-height: 16px;
      }
      img {
        line-height: 40px;
      }
    }
  }
  &.view-no-room {
    td {
      vertical-align: middle;
    }
  }
  &.view-room-mapping {
    th {
      padding-right: 11px;
      padding-left: 11px;
      @media (min-width: 1024px) {
        padding-right: 22px;
        padding-left: 22px;
      }
    }
    td {
      border-left-width: 1px;
      padding-right: 11px;
      padding-left: 11px;
      @media (min-width: 1024px) {
        padding-right: 22px;
        padding-left: 22px;
      }
      &[rowspan] {
        border-left-width: 0;
        vertical-align: top;
        padding-top: 11px + 13px;
        padding-bottom: 11px + 13px;
        h2 {
          font-size: 17px;
          font-weight: bold;
          line-height: 21px;
          color: #32325a;
          & + * {margin-top: 23px;}
        }
        .room-type-visual {
          cursor: pointer;
          & + * {margin-top: 23px;}
        }
        .list-facilities {
          display: flex;
          flex-direction: column;
          &__item + * {
            margin-top: 15px;
          }
        }
      }
    }
    tr {
      td {
        background-clip: padding-box;
      }
      &.has-no-dynamic-markup-price {
        & + .has-no-dynamic-markup-price {
          td {
            border-top-color: #eef1f6;
          }
        }
        & + tr:not(.has-no-dynamic-markup-price) {
          td {
            border-top-width: 1px;
          }
        }
        td {
          background-color: #eef1f6;
          & + td {border-left-color: #eef1f6;}
        }
      }
      &.has-dynamic-markup-price {
        td {
          &.cell-price {
            .price {
              font-size: 14px;
              line-height: 20px;
            }
          }
        }
      }
      &.has-no-dynamic-markup {
        display: none;
        & > td {
          opacity: 0.4;
        }
        &:first-child > td:first-child {opacity: 1;}
        cursor: default;

      }
    }
  }
  &.view-room-mapping .empty-row td {
    padding: 0;
    height: auto;
  }
  .price {
    font-size: 17px;
    font-weight: bold;
    color: #32325a;
    &.is-crossed-out {
      position: relative;
      color: #6e7c91;
      &:after {
        content: '';
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        background: #ff7373;
        height: 2px;
        left: 0;
        right: 0;
      }
    }
    &.is-dynamic-markup-price {
      position: relative;
      color: #56b282;
      cursor: pointer;
      &:after {
        content: '';
        position: absolute;
        top: 100%;
        left: 0;
        right: 0;
        background-image: linear-gradient(to right, currentColor 33%, transparent 0%);
        background-position: bottom;
        background-size: 4px 2px;
        background-repeat: repeat-x;
        height: 2px;
      }
    }
  }
}

.logo-supplier {
  display: inline-block;
  height: 40px;
  line-height: 40px;
  text-align: center;
}

.no-room-visual {
  $ratio: (125 / 188) * 100%;
  padding-top: $ratio;
  position: relative;
  img {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    object-fit: cover;
  }
}
.room-type-visual {
  $ratio: (372 / 529) * 100%;
  padding-top: #{$ratio};
  position: relative;
  img {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    object-fit: cover;
  }
}
.dynamic-ribbon {
  font-size: 8px;
  line-height: 12px;
  padding: 1px 3px 0;
  color: #fff;
  text-transform: uppercase;
  font-weight: bold;
  border-radius: 3px;
  position: absolute;
  top: 5px;
  right: 5px;
  &.is-net {
    background-color: #56b282;
  }
  &.is-gross {
    background-color: #ffa939;
  }
}

.link-close {
  position: absolute;
  top: 0;
  right: 0;
  font-size: 0;
  line-height: 0;
  width: 30px;
  transform: translate(50%, -33%);
  height: 30px;
  color: #aaa;
  &:before,
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    background: currentColor;
    left: 50%;
    width: 20px;
    height: 3px;
  }
  &:before {
    transform: translate(-50%, -50%) rotate(45deg);
  }
  &:after {
    transform: translate(-50%, -50%) rotate(-45deg);
  }
}
</style>
